import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { getUserprint } from '@narvar/userprint';
import {
  AnalyticsPayload,
  AnalyticsPayloadAutomatic,
  NoflakePayload,
  NoflakeTag,
  Config,
} from './noflakeSchema';
import Storage, { ErrorCallback, Guid, Keyspace, StorageType } from './storage';
import decamelize from './decamelize';

export interface AnalyticsContextType {
  data: Omit<AnalyticsPayload, AnalyticsPayloadAutomatic | 'eventType'>;
  isHipaaCompliant?: boolean;
  noflakeUrl: string;
  errorCallback: ErrorCallback;
  skip?: boolean;
}

export enum NarvarEventTypes {
  CLICK = 'click',
  COMPONENT_RENDER = 'component_render',
  PAGE_LOAD = 'page_load',
  WEB_VITAL = 'web_vital',
  SESSION_START = 'session_start',
  ERROR = 'error',
  EXPERIMENT = 'experiment',
  FEATURE_USAGE = 'feature_usage',
  SOCKET_DISCONNECT = 'socket_disconnect',
  SOCKET_CONNECT = 'socket_connect',
  TRACK_QUALITY = 'track_quality',
  CIRCUIT_BREAKER_OPEN = 'circuit_breaker_open',
}

export enum NarvarClickTypes {
  OUTGOING = 'outgoing',
  INTERACTION = 'interaction',
}

export enum NarvarClickSubTypes {
  REOPEN = 'reopen',
  DISMISS = 'dismiss',
  EXTERNAL_HELP_LINK = 'external_help_link',
  EXTERNAL_RETAILER_LINK = 'external_retailer_link',
  EXTERNAL_TRACKING_LINK = 'external_tracking_link',
  EXTERNAL_LINK = 'external_link',
  HIDE_SHIPPING_HISTORY = 'hide_shipping_history',
  LOGO_ASSET = 'logo_asset',
  COPY_LINK = 'copy_link',
  SEND_EMAIL = 'send_email',
  MARKETING_ASSET = 'marketing_asset',
  MORE_INFORMATION_LINK = 'more_information_link',
  NAVIGATION_ELEMENT = 'navigation_element',
  SHOW_SHARING_OPTIONS = 'show_sharing_options',
  PICKUP_LOCATION_DIRECTIONS_LINK = 'pickup_location_directions_link',
  PICKUP_LOCATION_PHONE_LINK = 'pickup_location_phone_link',
  PICKUP_LOCATION_SHOW_HOURS_LINK = 'pickup_location_show_hours_link',
  PICKUP_LOCATION_HIDE_HOURS_LINK = 'pickup_location_hide_hours_link',
  RATING = 'rating',
  SHOW_SHIPPING_HISTORY = 'show_shipping_history',
  SHARE_LINK = 'share_link',
  SMS_NUMBER_UPDATE = 'sms_number_update',
  SMS_SIGNUP = 'sms_signup',
  SMS_SIGNUP_AGAIN = 'sms_signup_again',
  SMS_UNSUBSCRIBE = 'sms_unsubscribe',
  EMAIL_UPDATE = 'email_update',
  EMAIL_SIGNUP = 'email_signup',
  EMAIL_SIGNUP_AGAIN = 'email_signup_again',
  EMAIL_UNSUBSCRIBE = 'email_unsubscribe',
  LINE_SIGNUP = 'line_signup',
  LINE_UNSUBSCRIBE = 'line_unsubscribe',
  LINE_SIGNUP_AGAIN = 'line_signup_again',
  SURVEY_SUBMIT = 'survey_submit',
  PRODUCT_RECOMMENDATIONS = 'product_recommendations',
  DELIVERY_INSTRUCTIONS = 'delivery_instructions',
  ITEMS_IN_THIS_SHIPMENT = 'items_in_this_shipment',
  SHIPMENTS = 'shipments',
  GIFT_TRACKING = 'gift_tracking',
  CALLING_DRIVER = 'calling_driver',
  TEXTING_DRIVER = 'texting_driver',
  VPOD_EXPAND = 'vpod_expand',
  VPOD_CONTRACT = 'vpod_contract',
  CHAT_INTERACTION = 'chat_interaction',
  CHAT_INPUT_SUBMIT = 'chat_input_submit',
  CHAT_CHIP_SELECT = 'chat_chip_select',
  ASSIST_PORTAL_LINK = 'assist_portal_link',
  HIGHLIGHT_TEXT = 'highlight_text',
  COPIED_TEXT = 'copied_text',
  TRACKING_NUMBER = 'tracking_number',
  REFRESH_SHIPPING_HISTORY = 'refresh_shipping_history',
}

export enum NarvarEventComponents {
  NTH_BLOCK_ASSET = 'nth-block-asset',
  NTH_BLOCK_BROADCAST_BANNER = 'nth-block-broadcast-banner',
  NTH_BLOCK_CARRIER_TRACKING_LINK = 'nth-block-carrier-tracking-link',
  NTH_BLOCK_CUSTOM_COMPONENT = 'nth-block-custom-component',
  NTH_BLOCK_DELIVERY_STATUS = 'nth-block-delivery-status',
  NTH_BLOCK_DRIVER_DETAILS = 'nth-block-driver-details',
  NTH_BLOCK_EDD = 'nth-block-edd',
  NTH_BLOCK_HELP_PILL = 'nth-block-help-pill',
  NTH_BLOCK_MAPTILER_MAP = 'nth-block-maptiler-map',
  NTH_BLOCK_NOTIFICATION_CENTER_DISCLAIMER_TEXT = 'nth-block-notification-center-disclaimer-text',
  NTH_BLOCK_SECTION_DIVIDER = 'nth-block-section-divider',
  NTH_BLOCK_STAR_RATING = 'nth-block-star-rating',
  NTH_BLOCK_TELEPHONE_INPUT = 'nth-block-telephone-input',
  NTH_BLOCK_TRACKING_STATUS = 'nth-block-tracking-status',
  NTH_BUNDLE_DYNAMIC_HEADER = 'nth-bundle-dynamic-header',
  NTH_BUNDLE_NARVAR_FOOTER = 'nth-bundle-narvar-footer',
  NTH_BUNDLE_SHIPPING_ACTIVITY = 'nth-bundle-shipping-activity',
  NTH_BUNDLE_VPOD = 'nth-bundle-vpod',
  NTH_KIT_DYNAMIC_LAYOUT_NEXT = 'nth-kit-dynamic-layout-next',
  NTH_PACK_EDD_PANEL = 'nth-pack-edd-panel',
  NTH_PACK_FEEDBACK = 'nth-pack-feedback',
  NTH_PACK_GIFT_TRACKING = 'nth-pack-gift-tracking',
  NTH_PACK_NOTIFICATION_CENTER = 'nth-pack-notification-center',
  NTH_PACK_EMAIL_NOTIFICATION_CENTER = 'nth-pack-email-notification-center',
  NTH_PACK_APP_PUSH_NOTIFICATION_CENTER = 'nth-pack-app-push-notification-center',
  NTH_PACK_LINE_NOTIFICATION_CENTER = 'nth-pack-line-notification-center',
  NTH_PACK_ITEMS_IN_THIS_SHIPMENT = 'nth-pack-items-in-this-shipment',
  NTH_PACK_MORE_INFORMATION = 'nth-pack-more-information',
  NTH_PACK_PICKUP_LOCATION = 'nth-pack-pickup-location',
  NTH_PACK_SHIPMENTS = 'nth-pack-shipments',
  NTH_KIT_CHAT = 'nth-kit-chat',
  APP_ROUTER_TRACKING = 'app-router-tracking',
  CUSTOM_COMPONENT_DRAWER = 'custom-component-drawer',
}

const MULTIPLE_COMPONENTS = 'multiple-components';
const MULTIPLE_FEATURES = 'multiple-features';

export const DEFAULT_ANALYTICS_CONTEXT: AnalyticsContextType = {
  data: {
    eventSchema: '',
    pageRetailerMoniker: '',
    productName: '',
    tag: NoflakeTag.BAD,
  },
  isHipaaCompliant: false,
  noflakeUrl: 'https://titanlite-235686439465.us-central1.run.app/v2/noflake',
  errorCallback: (err) => console.error(err),
};

export const QUEUE_INTERVAL = 1;
export const COMPONENT_RENDER_QUEUE_INTERVAL = 5000;
let componentRenderEvent: AnalyticsContextType | null;
const eventQueue: AnalyticsContextType[] = [];
let dequeueing = false;
const guidStorage = Storage(StorageType.LOCAL_STORAGE, Keyspace.GUID);
const requestUuidStorage = Storage(
  StorageType.SESSION_STORAGE,
  Keyspace.REQUEST_UUID,
);

const postToNoflake = async ({
  data,
  isHipaaCompliant,
  noflakeUrl,
  errorCallback,
}: AnalyticsContextType): Promise<void> => {
  const { id, dateCreated: guidDateCreated = null } =
    guidStorage.get<Guid>(errorCallback) || {};
  const guid = id || '';
  const requestUuid = requestUuidStorage.get<string>(errorCallback) || null;

  const {
    browser_fingerprint: clientBrowserFingerprint,
    browser_name: clientBrowserName,
    browser_version: clientBrowserVersion,
    device_mem_gb: clientDeviceMemGb,
    device_model: clientDeviceModel,
    device_net_speed_mbps: clientDeviceNetSpeedMbps,
    device_net_type: clientDeviceNetType,
    device_num_processors: clientDeviceNumProcessors,
    device_size_px: clientDeviceSizePx,
    device_touchpoints: clientDeviceTouchpoints,
    device_type: clientDeviceType,
    device_vendor: clientDeviceVendor,
    language: clientBrowserLanguage,
    os_name: clientDeviceOsName,
    os_version: clientDeviceOsVersion,
    time_offset_mins_to_utc: clientDeviceTimeOffsetMinsToUtc,
  } = (await getUserprint()) as {
    browser_fingerprint: NonNullable<
      AnalyticsPayload['clientBrowserFingerprint']
    > | null;
    browser_name: NonNullable<AnalyticsPayload['clientBrowserName']> | null;
    browser_version: NonNullable<
      AnalyticsPayload['clientBrowserVersion']
    > | null;
    device_mem_gb: NonNullable<AnalyticsPayload['clientDeviceMemGb']> | null;
    device_model: NonNullable<AnalyticsPayload['clientDeviceModel']> | null;
    device_net_speed_mbps: NonNullable<
      AnalyticsPayload['clientDeviceNetSpeedMbps']
    > | null;
    device_net_type: NonNullable<
      AnalyticsPayload['clientDeviceNetType']
    > | null;
    device_num_processors: NonNullable<
      AnalyticsPayload['clientDeviceNumProcessors']
    > | null;
    device_size_px: NonNullable<AnalyticsPayload['clientDeviceSizePx']> | null;
    device_touchpoints: NonNullable<
      AnalyticsPayload['clientDeviceTouchpoints']
    > | null;
    device_type: NonNullable<AnalyticsPayload['clientDeviceType']> | null;
    device_vendor: NonNullable<AnalyticsPayload['clientDeviceVendor']> | null;
    language: NonNullable<AnalyticsPayload['clientBrowserLanguage']> | null;
    os_name: NonNullable<AnalyticsPayload['clientDeviceOsName']> | null;
    os_version: NonNullable<AnalyticsPayload['clientDeviceOsVersion']> | null;
    time_offset_mins_to_utc: AnalyticsPayload['clientDeviceTimeOffsetMinsToUtc'];
  };

  const {
    document: { referrer: pageReferrerUrl, title: pageTitle },
    location: { href: pageUrl },
    navigator: { vendor: clientDeviceNavigatorVendor = null },
    origin: pageHost,
  } = window;

  const providedData: Required<
    Pick<AnalyticsPayload, AnalyticsPayloadAutomatic>
  > = {
    clientBrowserFingerprint,
    clientBrowserLanguage,
    clientBrowserName,
    clientBrowserVersion,
    clientDeviceMemGb,
    clientDeviceModel,
    clientDeviceNetSpeedMbps,
    clientDeviceNetType,
    clientDeviceNumProcessors,
    clientDeviceOsName,
    clientDeviceOsVersion,
    clientDeviceSizePx,
    clientDeviceTimeOffsetMinsToUtc,
    clientDeviceTouchpoints,
    clientDeviceType,
    clientDeviceVendor: clientDeviceVendor || clientDeviceNavigatorVendor,
    deviceId: guid,
    eventTs: new Date().toISOString(),
    guid,
    guidDateCreated,
    pageHost,
    pageReferrerUrl,
    pageTitle,
    pageUrl,
    requestUuid,
  };

  const payload: Required<AnalyticsPayload> = {
    // Required
    eventType: '',

    // Optional
    bannerSegmentId: null,
    bannerSegmentName: null,
    chatClientSessionId: null,
    clickAssetId: null,
    clickAssetMultilinkId: null,
    clickAssetSchdEndDateUtc: null,
    clickAssetSchdStartDateUtc: null,
    clickAssetType: null,
    clickAssetUrl: null,
    clickCategory: null,
    clickDeliveryInstructionsComment: null,
    clickDeliveryInstructionsLocation: null,
    clickLinkDestinationUrl: null,
    clickLinkLabel: null,
    clickLocationId: null,
    clickNotificationIsChangeNumber: null,
    clickNotificationIsOptIn: null,
    clickNotificationIsOptOut: null,
    clickNotificationMarketingOptInChecked: null,
    clickNotificationOptInDisclaimerText: null,
    clickNotificationMarketingPrivacyLink: null,
    clickNotificationMarketingTermsLink: null,
    clickNotificationMarketingOptInText: null,
    clickNotificationIsOrderLevelSignup: null,
    clickProductRecItemData: null,
    clickProductRecItemIndex: null,
    clickProductRecItemName: null,
    clickRedirectSuccessLocation: null,
    clickRedirectSuccessName: null,
    clickSubType: null,
    clickSurveyComment: null,
    clickSurveyRating: null,
    clickType: null,
    clientCity: null,
    clientCountry: null,
    clientLat: null,
    clientLong: null,
    clientRegion: null,
    config: null,
    customerId: null,
    displayedEddBegin: null,
    displayedEddEnd: null,
    eddTimeRangeBegin: null,
    eddTimeRangeEnd: null,
    eddDisplayedTimeRange: null,
    displayedLocations: null,
    eventCarrierMoniker: null,
    eventComponent: null,
    eventFeature: null,
    eventId: null,
    eventItemGroup: null,
    eventNotificationSignUpError: null,
    eventNotificationSmsValidationError: null,
    eventNotificationEmailValidationError: null,
    eventTrackStatus: null,
    eventTrackSubStatus: null,
    eventTrackingNumber: null,
    feature: null,
    ingestionTimestamp: null,
    isFallbackView: null,
    orderNumber: null,
    pageCarrierService: null,
    pageComponent: null,
    pageEddFormatted: null,
    pageHasConciergeSelected: null,
    pageHasEdd: null,
    pageLoadMs: null,
    pageLocaleCountry: null,
    pageLocaleLanguage: null,
    pageOrderNumber: null,
    pageShipmentStatus: null,
    pageTrackType: null,
    pageUiLoadMs: null,
    pageUrlLocale: null,
    pageVersionName: null,
    provider: null,
    requestPage: null,
    requestSource: null,
    segmentId: null,
    segmentName: null,
    trackEddBegin: null,
    trackEddEnd: null,
    trackEddSource: null,
    trackLayout: null,
    zipcodeLocation: null,

    // Data from context
    ...data,

    // Data provided automatically
    ...providedData,
  };

  const allowedFieldsForHipaa = [
    'bannerSegmentId',
    'bannerSegmentName',
    'clickAssetId',
    'clickAssetMultilinkId',
    'clickAssetSchdEndDateUtc',
    'clickAssetSchdStartDateUtc',
    'clickAssetType',
    'clickAssetUrl',
    'clickCategory',
    'clickLinkLabel',
    'clickNotificationIsChangeNumber',
    'clickNotificationIsOptIn',
    'clickNotificationIsOptOut',
    'clickNotificationMarketingOptInChecked',
    'clickNotificationOptInDisclaimerText',
    'clickNotificationMarketingPrivacyLink',
    'clickNotificationMarketingTermsLink',
    'clickNotificationMarketingOptInText',
    'clickNotificationIsOrderLevelSignup',
    'clickProductRecItemData',
    'clickProductRecItemIndex',
    'clickProductRecItemName',
    'clickSubType',
    'clickSurveyComment',
    'clickSurveyRating',
    'clickType',
    'clientCountry',
    'config',
    'eventCarrierMoniker',
    'eventComponent',
    'eventFeature',
    'eventId',
    'eventItemGroup',
    'eventSchema',
    'eventTrackStatus',
    'eventTrackSubStatus',
    'eventTs',
    'eventType',
    'feature',
    'guidDateCreated',
    'ingestionTimestamp',
    'isFallbackView',
    'pageCarrierService',
    'pageComponent',
    'pageEddFormatted',
    'pageHasConciergeSelected',
    'pageHasEdd',
    'pageHost',
    'pageLoadMs',
    'pageLocaleCountry',
    'pageRetailerMoniker',
    'pageShipmentStatus',
    'pageTitle',
    'pageTrackType',
    'pageVersionName',
    'productName',
    'provider',
    'requestPage',
    'requestSource',
    'requestUuid',
    'segmentId',
    'segmentName',
    'tag',
    'trackLayout',
  ];

  // Merge analytics payload with noflake schema, decamelizing at the same time
  const noflakePayload = Object.entries(payload).reduce(
    (
      acc: {
        [key: string]: NoflakePayload[keyof NoflakePayload];
      },
      [camelKey, value],
    ) => {
      // We disallow certain fields from being sent to noflake if the retailer isHipaaCompliant.
      if (isHipaaCompliant && !allowedFieldsForHipaa.includes(camelKey)) {
        return acc;
      }
      const [snakeKey, noFlakeValue] = decamelize(camelKey, value);
      acc[snakeKey] = noFlakeValue;
      return acc;
    },
    {},
  );

  try {
    // `keepalive: true` allows our analytics requests to outlive the current page
    await fetch(noflakeUrl, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noflakePayload),
    });
  } catch (err) {
    errorCallback(err);
  }
};

const dequeue = async (): Promise<void> => {
  const analyticsEvent: AnalyticsContextType = eventQueue[0];

  if (
    analyticsEvent &&
    analyticsEvent.data !== DEFAULT_ANALYTICS_CONTEXT.data &&
    !dequeueing
  ) {
    try {
      dequeueing = true;
      await postToNoflake(analyticsEvent);
      eventQueue.shift();
      dequeueing = false;
    } catch (err) {
      console.error('Error posting to noflake:', err);
    }
  }
};

let dequeInterval = setInterval(dequeue, QUEUE_INTERVAL);

const enqueueComponentRender = (): void => {
  if (!componentRenderEvent) return;
  eventQueue.push(componentRenderEvent);
  componentRenderEvent = null;
};

let enqueueComponentRenderInterval = setInterval(
  enqueueComponentRender,
  COMPONENT_RENDER_QUEUE_INTERVAL,
);

export const setDequeInterval = (ms: number): void => {
  clearInterval(dequeInterval);
  dequeInterval = setInterval(dequeue, ms);
};

export const setEnqueueComponentRenderInterval = (ms: number): void => {
  clearInterval(enqueueComponentRenderInterval);
  enqueueComponentRenderInterval = setInterval(enqueueComponentRender, ms);
};

export const flushAnalyticsQueue = async (): Promise<void> => {
  while (eventQueue.length > 0) {
    await postToNoflake(eventQueue[0]);
    eventQueue.shift();
  }
};

// Useful for firing off all queued events left when navigating away from
// the page in the same browser window. Combined with { keep-alive: true }
// used in postToNoflake, we can fire all remaining analytics events
// essentially all at once, and have confidence they'll complete without
// having to await them
export const flushAnalyticsQueueSync = (): void => {
  while (eventQueue.length > 0) {
    postToNoflake(eventQueue[0]).catch((err) => console.log(err));
    eventQueue.shift();
  }
};

export const AnalyticsContext = createContext(DEFAULT_ANALYTICS_CONTEXT);

export const useAnalyticsContext = () => useContext(AnalyticsContext);

export const AnalyticsProvider = AnalyticsContext.Provider;

export const useAnalyticsPost = (): ((
  data: Partial<AnalyticsPayload> & Pick<AnalyticsPayload, 'eventType'>,
) => void) => {
  const context = useContext(AnalyticsContext);

  const logEvent = useCallback(
    (
      data: Partial<AnalyticsPayload> & Pick<AnalyticsPayload, 'eventType'>,
    ): void => {
      if (typeof window === 'undefined') return;

      const {
        data: contextData,
        noflakeUrl,
        isHipaaCompliant,
        errorCallback,
        skip = false,
      } = context;

      if (skip === true) return;

      const s4 = (): string =>
        Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      const id = `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4() + s4() + s4()}`;

      // guid will be set the first time we initialize analytics context
      if (
        guidStorage.isEmpty(errorCallback) ||
        guidStorage.isExpired(errorCallback)
      ) {
        guidStorage.set(
          {
            id,
            dateCreated: new Date().toISOString(),
          },
          errorCallback,
        );
      }

      if (requestUuidStorage.isEmpty(errorCallback)) {
        requestUuidStorage.set(id, errorCallback);
      }

      if (
        data.eventType === NarvarEventTypes.COMPONENT_RENDER &&
        contextData.eventSchema === NoflakeTag.COMPONENT_PLATFORM
      ) {
        const { data: componentRenderData, data: { config = [] } = {} } =
          componentRenderEvent || {};

        // Merge all component_render events into one main payload.
        // The ...data spread has the potential to overwrite keys from previous
        // payloads, but that's cool since the merged component_render payload
        // shouldn't be used for looking at component details anyways. We just
        // want as much info as possible.
        const renderConfigData: Config = {
          name: data.feature || 'feature name not specified by render context',
          options: [`nth-source:${data?.eventComponent ?? ''}`],
        };

        componentRenderEvent = {
          data: {
            ...componentRenderData,
            ...contextData,
            ...data,
            config:
              config && config.length
                ? [...config, renderConfigData]
                : [renderConfigData],
            ...(config &&
              config.length && {
                eventComponent: MULTIPLE_COMPONENTS,
                eventFeature: MULTIPLE_COMPONENTS,
                feature: MULTIPLE_FEATURES,
                pageComponent: MULTIPLE_COMPONENTS,
              }),
          },
          noflakeUrl,
          errorCallback,
        };
        return;
      }

      eventQueue.push({
        data: {
          ...contextData,
          ...data,
        },
        isHipaaCompliant,
        noflakeUrl,
        errorCallback,
      });
    },
    [context],
  );

  const logEventRef = useRef(logEvent);

  useEffect(() => {
    logEventRef.current = logEvent;
  }, [logEventRef, logEvent]);

  return useCallback<typeof logEvent>(
    (...args) => logEventRef.current(...args),
    [logEventRef],
  );
};
